import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Trash2, Plus, Check, X, Lock } from "lucide-react";
import { toast } from "sonner";

export function ManageStagesDialog({ open, onOpenChange, stages, onChanged }) {
  const [rows, setRows] = useState(stages || []);
  const [labels, setLabels] = useState({});
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null); // {key, count}
  const [reassignTo, setReassignTo] = useState("");

  useEffect(() => { if (open) { setRows(stages || []); setLabels({}); setNewLabel(""); setRemoving(null); setReassignTo(""); } }, [open, stages]);

  const opens = rows.filter((s) => s.type === "open");

  const refresh = async (data) => { if (data?.stages) setRows(data.stages); onChanged?.(data); };
  const call = async (fn) => { setBusy(true); try { const { data } = await fn(); await refresh(data); return data; } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };

  const addStage = async () => {
    const label = newLabel.trim(); if (!label) return;
    const data = await call(() => api.post("/pipeline/stages", { label }));
    if (data) { setNewLabel(""); toast.success(`Added "${label}"`); }
  };
  const rename = async (key) => {
    const label = (labels[key] ?? "").trim();
    const current = rows.find((s) => s.key === key)?.label;
    if (!label || label === current) { setLabels((l) => { const n = { ...l }; delete n[key]; return n; }); return; }
    const data = await call(() => api.patch(`/pipeline/stages/${key}`, { label }));
    if (data) { setLabels((l) => { const n = { ...l }; delete n[key]; return n; }); toast.success("Renamed"); }
  };
  const move = async (key, dir) => {
    const order = opens.map((s) => s.key);
    const i = order.indexOf(key); const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    await call(() => api.post("/pipeline/stages/reorder", { keys: order }));
  };
  const confirmRemove = async () => {
    const payload = removing.count > 0 ? { reassign_to: reassignTo } : {};
    if (removing.count > 0 && !reassignTo) { toast.error("Choose a stage to move the opportunities to."); return; }
    const data = await call(() => api.delete(`/pipeline/stages/${removing.key}`, { data: payload }));
    if (data) { toast.success(data.moved ? `Removed · moved ${data.moved} opportunit${data.moved === 1 ? "y" : "ies"}` : "Stage removed"); setRemoving(null); setReassignTo(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="manage-stages-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage pipeline stages</DialogTitle>
          <DialogDescription>Add, rename or reorder your pipeline. Removing a stage that holds opportunities moves them to a stage you choose, so nothing is lost. Won and Lost are fixed outcomes.</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
          {rows.map((s) => {
            const locked = s.type !== "open";
            const idx = opens.findIndex((o) => o.key === s.key);
            const edited = labels[s.key] !== undefined && labels[s.key] !== s.label;
            return (
              <div key={s.key} className="flex items-center gap-2 rounded-md border border-border p-2" data-testid={`manage-stage-${s.key}`}>
                <span className={`h-2 w-2 rounded-full shrink-0 ${s.type === "won" ? "bg-[hsl(var(--success))]" : s.type === "lost" ? "bg-[hsl(var(--danger))]" : "bg-[hsl(var(--primary))]"}`} />
                {locked ? (
                  <div className="flex-1 flex items-center gap-2 text-sm font-medium text-muted-foreground"><Lock className="h-3.5 w-3.5" />{s.label} <span className="meta">· {s.count} · fixed</span></div>
                ) : (
                  <>
                    <Input value={labels[s.key] ?? s.label} onChange={(e) => setLabels((l) => ({ ...l, [s.key]: e.target.value }))}
                      onBlur={() => edited && rename(s.key)} onKeyDown={(e) => e.key === "Enter" && rename(s.key)}
                      className="h-8 flex-1" disabled={busy} data-testid={`manage-stage-input-${s.key}`} />
                    <span className="meta nums w-8 text-right">{s.count}</span>
                    {edited && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => rename(s.key)} disabled={busy}><Check className="h-4 w-4 text-[hsl(var(--success))]" /></Button>}
                    <div className="flex flex-col">
                      <button className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={busy || idx === 0} onClick={() => move(s.key, -1)} data-testid={`manage-stage-up-${s.key}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={busy || idx === opens.length - 1} onClick={() => move(s.key, 1)} data-testid={`manage-stage-down-${s.key}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[hsl(var(--danger))]" disabled={busy || opens.length <= 1}
                      onClick={() => { setRemoving({ key: s.key, label: s.label, count: s.count }); setReassignTo(""); }} data-testid={`manage-stage-remove-${s.key}`}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {removing && (
          <div className="rounded-md border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger-bg))] p-3 space-y-2" data-testid="manage-stage-remove-confirm">
            <div className="text-sm font-medium">Remove "{removing.label}"?</div>
            {removing.count > 0 ? (
              <div className="space-y-2">
                <p className="meta">{removing.count} opportunit{removing.count === 1 ? "y is" : "ies are"} in this stage. Move them to:</p>
                <Select value={reassignTo} onValueChange={setReassignTo}>
                  <SelectTrigger className="h-8 bg-background" data-testid="manage-stage-reassign-select"><SelectValue placeholder="Choose a stage" /></SelectTrigger>
                  <SelectContent>{rows.filter((s) => s.key !== removing.key).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : <p className="meta">This stage is empty.</p>}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setRemoving(null)} disabled={busy}><X className="h-4 w-4 mr-1" />Cancel</Button>
              <Button size="sm" className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger)/0.9)]" onClick={confirmRemove} disabled={busy} data-testid="manage-stage-remove-apply">Remove stage</Button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 border-t border-border pt-3">
          <Input placeholder="New stage name" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStage()} className="h-9" disabled={busy} data-testid="manage-stage-new-input" />
          <Button onClick={addStage} disabled={busy || !newLabel.trim()} data-testid="manage-stage-add"><Plus className="h-4 w-4 mr-1" />Add stage</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
